import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { useCompetition } from './CompetitionContext';
import { compareEventsByDisplayOrder } from '../utils/eventSort';
import '../css/draw-export-pdf.css';

const MEETING_SCOPED_BASE_PATHS = new Set([
    'live-timetable', 'draw', 'draw-export-pdf', 'racecards', 'race-cards',
    'competition-admin', 'entry-admin', 'events-admin', 'banding-helper', 'victor-ludorum',
    'system-settings', 'registration-desk', 'racing-dashboard',
    'commentary', 'club-summary'
]);

function buildMeetingScopedPath(pathname) {
    const currentPathname = String(window.location.pathname || '');
    const currentSearch = String(window.location.search || '');
    const currentSegments = currentPathname.split('/').filter(Boolean);
    const activeSlug = (currentSegments.length >= 2 && MEETING_SCOPED_BASE_PATHS.has(currentSegments[1]))
        ? String(currentSegments[0] || '').trim().toLowerCase()
        : '';

    const targetSegments = String(pathname || '').split('/').filter(Boolean);
    const targetIsMeetingScoped = targetSegments.length >= 1 && MEETING_SCOPED_BASE_PATHS.has(targetSegments[0]);
    const scopedPathname = (activeSlug && targetIsMeetingScoped)
        ? `/${activeSlug}/${targetSegments.join('/')}`
        : pathname;

    const params = new URLSearchParams(currentSearch);
    const rawMeetingId = String(params.get('meetingId') || '').trim();
    const meetingId = Number.parseInt(rawMeetingId, 10);
    const meetingSearch = Number.isFinite(meetingId) && meetingId > 0 ? `?meetingId=${meetingId}` : '';

    return `${scopedPathname}${meetingSearch}`;
}

function hasMeaningfulValue(value) {
    if (value === null || value === undefined) return false;
    const text = String(value).trim();
    return text !== '' && text.toUpperCase() !== 'NULL';
}

function cleanText(value, fallback = '') {
    if (!hasMeaningfulValue(value)) return fallback;
    return String(value).replace(/[\r\n]+/g, ' ').trim();
}

function normalizeClubValue(value) {
    return cleanText(value).toLowerCase();
}

function parseRaceNo(value) {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : Number.MAX_SAFE_INTEGER;
}

function parseRaceTime(value) {
    const text = cleanText(value, '');
    const match = text.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (!match) return Number.MAX_SAFE_INTEGER;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    const seconds = match[3] ? Number(match[3]) : 0;
    return (hours * 3600) + (minutes * 60) + seconds;
}

function formatLaneDisplay(race, lane) {
    const club = cleanText(lane?.titleFull || lane?.title, 'TBC');
    if (/^WOR\s+/i.test(club) || /^(TBC|BYE)$/i.test(club)) {
        return club;
    }

    const competitorNo = cleanText(lane?.competitorNo, '');
    const crew = cleanText(lane?.suffix, '');
    const mastersCategory = cleanText(lane?.mastersCategory, '').toUpperCase();
    const parenParts = [];
    if (race?.event?.handicapEvent && mastersCategory) parenParts.push(`Mas${mastersCategory}`);
    if (crew) parenParts.push(crew);
    const suffix = parenParts.length ? ` (${parenParts.join(' - ')})` : '';

    const base = competitorNo ? `${competitorNo} - ${club}` : club;
    return `${base}${suffix}`;
}

function formatRaceTime(value) {
    const text = cleanText(value, '');
    if (!text) return '--:--';

    const hhmmss = text.match(/^(\d{1,2}:\d{2})(?::\d{2}(?:\.\d+)?)?$/);
    if (hhmmss) return hhmmss[1];

    return text;
}

function extractFilename(disposition, fallback) {
    const value = String(disposition || '');
    const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match) {
        try {
            return decodeURIComponent(utf8Match[1]);
        } catch (_error) {
            return utf8Match[1];
        }
    }

    const quotedMatch = value.match(/filename="?([^";]+)"?/i);
    if (quotedMatch) return quotedMatch[1];
    return fallback;
}

export function DrawExportPdf() {
    const history = useHistory();
    const { getToken, loading: authLoading } = useAuth();
    const { competitionName } = useCompetition();

    const [races, setRaces] = useState([]);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [selectedClub, setSelectedClub] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const fetchRaces = useCallback(async () => {
        setLoading(true);
        setErrorMessage('');
        try {
            const token = getToken();
            const response = await fetch('/api/timetable', {
                credentials: 'include',
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });

            if (!response.ok) {
                throw new Error(`Failed to load draw data (HTTP ${response.status}).`);
            }

            const payload = await response.json();
            setRaces(Array.isArray(payload) ? payload : []);
        } catch (error) {
            console.error(error);
            setErrorMessage(error.message || 'Failed to load draw data.');
        } finally {
            setLoading(false);
        }
    }, [getToken]);

    useEffect(() => {
        if (authLoading) return;
        fetchRaces();
    }, [authLoading, fetchRaces]);

    const clubOptions = useMemo(() => {
        const values = new Set();

        races.forEach(race => {
            if (Number(race?.event?.id || 0) <= 0) return;
            const laneA = cleanText(race?.laneA?.titleFull || race?.laneA?.title, '');
            const laneB = cleanText(race?.laneB?.titleFull || race?.laneB?.title, '');
            if (laneA) values.add(laneA);
            if (laneB) values.add(laneB);
        });

        return [...values].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    }, [races]);

    const filteredRaces = useMemo(() => {
        const selectedClubKey = normalizeClubValue(selectedClub);

        return races
            .filter(race => Number(race?.event?.id || 0) > 0)
            .filter(race => {
                if (!selectedClubKey) return true;
                const laneAKey = normalizeClubValue(race?.laneA?.titleFull || race?.laneA?.title);
                const laneBKey = normalizeClubValue(race?.laneB?.titleFull || race?.laneB?.title);
                return laneAKey === selectedClubKey || laneBKey === selectedClubKey;
            })
            .sort((a, b) => {
                const timeDiff = parseRaceTime(a?.raceTimeFormatted || a?.raceTime)
                    - parseRaceTime(b?.raceTimeFormatted || b?.raceTime);
                if (timeDiff !== 0) return timeDiff;
                return parseRaceNo(a?.raceNo) - parseRaceNo(b?.raceNo);
            });
    }, [races, selectedClub]);

    const groupedEvents = useMemo(() => {
        const eventMap = new Map();
        const order = [];

        filteredRaces.forEach(race => {
            const eventId = Number(race?.event?.id || 0);
            if (eventId <= 0) return;
            if (!eventMap.has(eventId)) {
                eventMap.set(eventId, {
                    event: race.event,
                    sponsor: cleanText(race.sponsorTitle, ''),
                    races: []
                });
                order.push(eventId);
            }
            eventMap.get(eventId).races.push(race);
        });

        order.sort((idA, idB) =>
            compareEventsByDisplayOrder(eventMap.get(idA).event, eventMap.get(idB).event));

        return order.map(id => eventMap.get(id));
    }, [filteredRaces]);

    const summaryText = useMemo(() => {
        const raceCount = filteredRaces.length;
        const eventCount = groupedEvents.length;
        if (!raceCount) return 'No races found for this filter.';
        return `${eventCount} event${eventCount === 1 ? '' : 's'} and ${raceCount} race${raceCount === 1 ? '' : 's'} ready for export.`;
    }, [filteredRaces, groupedEvents]);

    const downloadPdf = useCallback(async () => {
        setDownloading(true);
        setErrorMessage('');
        try {
            const token = getToken();
            const params = new URLSearchParams();
            if (cleanText(selectedClub, '')) params.set('club', selectedClub);
            const query = params.toString();
            const endpoint = query ? `/api/draw/export/pdf?${query}` : '/api/draw/export/pdf';

            const response = await fetch(endpoint, {
                credentials: 'include',
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });

            if (!response.ok) {
                throw new Error(`PDF export failed (HTTP ${response.status}).`);
            }

            const blob = await response.blob();
            const fallbackName = selectedClub ? 'draw-extract-filtered.pdf' : 'draw-extract.pdf';
            const fileName = extractFilename(response.headers.get('content-disposition'), fallbackName);
            const blobUrl = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = blobUrl;
            anchor.download = fileName;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error(error);
            setErrorMessage(error.message || 'PDF export failed.');
        } finally {
            setDownloading(false);
        }
    }, [getToken, selectedClub]);

    const goBack = useCallback(() => {
        history.push(buildMeetingScopedPath('/competition-admin'));
    }, [history]);

    return (
        <div className="draw-pdf-page">
            <header className="draw-pdf-header">
                <div>
                    <h1>Draw PDF Export</h1>
                    <p>Create a print-ready PDF copy of the draw using a dedicated export layout.</p>
                </div>
                <button className="btn btn-outline-secondary" onClick={goBack}>
                    Back to Competition Admin
                </button>
            </header>

            {errorMessage ? <Alert variant="danger">{errorMessage}</Alert> : null}

            <section className="draw-pdf-controls">
                <div className="draw-pdf-control-group">
                    <label htmlFor="draw-pdf-club">Club filter</label>
                    <select
                        id="draw-pdf-club"
                        className="form-select"
                        value={selectedClub}
                        onChange={(event) => setSelectedClub(event.target.value)}
                        disabled={loading || downloading}
                    >
                        <option value="">All clubs</option>
                        {clubOptions.map(club => (
                            <option key={club} value={club}>{club}</option>
                        ))}
                    </select>
                </div>
                <div className="draw-pdf-button-row">
                    <button
                        className="btn btn-outline-primary"
                        onClick={fetchRaces}
                        disabled={loading || downloading}
                    >
                        {loading ? 'Refreshing...' : 'Refresh Preview'}
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={downloadPdf}
                        disabled={loading || downloading || filteredRaces.length === 0}
                    >
                        {downloading ? 'Generating PDF...' : 'Download PDF'}
                    </button>
                </div>
            </section>

            <section className="draw-pdf-summary">
                <div className="draw-pdf-title">{competitionName || 'Regatta Draw'}</div>
                <div className="draw-pdf-meta">
                    <span>{summaryText}</span>
                    <span>{selectedClub ? `Filter: ${selectedClub}` : 'Filter: All clubs'}</span>
                </div>
            </section>

            <section className="draw-pdf-preview">
                {groupedEvents.map(group => (
                    <article className="draw-pdf-event" key={group.event?.id || group.event?.title}>
                        <div className="draw-pdf-event-head">
                            <h2>{cleanText(group.event?.title, 'Untitled Event').toUpperCase()}</h2>
                            {group.sponsor ? (
                                <span className="draw-pdf-sponsor">Sponsored By: {group.sponsor}</span>
                            ) : null}
                        </div>
                        <table className="draw-pdf-table">
                            <thead>
                                <tr>
                                    <th className="draw-pdf-col-no">No</th>
                                    <th className="draw-pdf-col-next">Next</th>
                                    <th className="draw-pdf-col-time">Time</th>
                                    <th className="draw-pdf-col-round">Round</th>
                                    <th>Middlesex (Enclosure) Station</th>
                                    <th>Surrey Station</th>
                                </tr>
                            </thead>
                            <tbody>
                                {group.races.map(race => (
                                    <tr key={race.raceId || `${race.raceNo}-${race.round}`}>
                                        <td>{cleanText(race.raceNo, '')}</td>
                                        <td>{cleanText(race.nextRaceNo, '')}</td>
                                        <td>{formatRaceTime(race.raceTimeFormatted || race.raceTime)}</td>
                                        <td>{cleanText(race.round, '')}</td>
                                        <td>{formatLaneDisplay(race, race.laneA)}</td>
                                        <td>{formatLaneDisplay(race, race.laneB)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </article>
                ))}
                {!loading && groupedEvents.length === 0 ? (
                    <div className="draw-pdf-empty">No draw rows match the selected filter.</div>
                ) : null}
            </section>
        </div>
    );
}
